import { useTranslation } from 'react-i18next';
import { ImagePlus } from 'lucide-react';
import { DropdownField } from '../common/DropdownField';
import { TextField } from '../common/TextField';
import { WebLocationPicker } from '../cv/forms/web-pickers';
import {
  COMPANY_INDUSTRY_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  type CompanyFormSection,
  type CompanyFormValues,
  toCompanySlug,
} from '../../lib/company-form';

interface CompanyFormFieldsProps {
  values: CompanyFormValues;
  onChange: (patch: Partial<CompanyFormValues>) => void;
  onSlugTouched: () => void;
  selectedLogo?: string;
  onPickLogo: () => void;
  section?: CompanyFormSection;
}

const LABEL: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
  marginBottom: 6,
  display: 'block',
};

const HINT: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--text-tertiary)',
  marginTop: 6,
  marginBottom: 0,
};

export function CompanyFormFields({
  values,
  onChange,
  onSlugTouched,
  selectedLogo,
  onPickLogo,
  section = 'all',
}: CompanyFormFieldsProps) {
  const { t } = useTranslation('auth');
  const showIdentity = section === 'all' || section === 'identity';
  const showDetails = section === 'all' || section === 'details';
  const showStory = section === 'all' || section === 'story';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {showIdentity ? (
        <>
          <TextField
            label={t('onboarding.companyInfo.companyName', {
              defaultValue: 'Company name *',
            })}
            value={values.name}
            onChange={(name) => onChange({ name })}
            placeholder={t('onboarding.companyInfo.companyNamePlaceholder', {
              defaultValue: 'Acme Inc.',
            })}
          />

          <div>
            <TextField
              label={t('onboarding.companyInfo.careerPageUrl', {
                defaultValue: 'Career page URL *',
              })}
              value={values.slug}
              onChange={(next) => {
                onSlugTouched();
                onChange({ slug: toCompanySlug(next) });
              }}
              placeholder="acmeinc"
              contentDirection="ltr"
              autoComplete="off"
              iconLeft={
                <span
                  style={{
                    color: 'var(--text-secondary)',
                    fontSize: 15,
                    whiteSpace: 'nowrap',
                  }}
                >
                  tabbio.com/
                </span>
              }
            />
            <p style={HINT}>
              {t('onboarding.companyInfo.urlHint', {
                defaultValue: 'This will be your public career page URL.',
              })}
            </p>
          </div>
        </>
      ) : null}

      {showDetails ? (
        <>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
          >
            <DropdownField
              label={t('onboarding.companyInfo.industry', {
                defaultValue: 'Industry *',
              })}
              value={values.industry}
              placeholder={t('onboarding.companyInfo.industryPlaceholder', {
                defaultValue: 'Select industry',
              })}
              options={[...COMPANY_INDUSTRY_OPTIONS]}
              onSelect={(industry) => onChange({ industry })}
              title={t('onboarding.companyInfo.industryPickerTitle', {
                defaultValue: 'Industry',
              })}
            />

            <DropdownField
              label={t('onboarding.companyInfo.companySize', {
                defaultValue: 'Company size *',
              })}
              value={values.employeeCount}
              placeholder={t('onboarding.companyInfo.companySizePlaceholder', {
                defaultValue: 'Select size',
              })}
              options={[...COMPANY_SIZE_OPTIONS]}
              onSelect={(employeeCount) => onChange({ employeeCount })}
              title={t('onboarding.companyInfo.companySizePickerTitle', {
                defaultValue: 'Company size',
              })}
            />
          </div>

          <div>
            <label style={LABEL}>
              {t('onboarding.companyInfo.location', {
                defaultValue: 'Location *',
              })}
            </label>
            <WebLocationPicker
              value={values.location}
              onSelect={(location) => onChange({ location })}
              placeholder={t('onboarding.companyInfo.locationPlaceholder', {
                defaultValue: 'e.g. Abu Dhabi or Dubai',
              })}
            />
            <p style={HINT}>
              {t('onboarding.companyInfo.locationHint', {
                defaultValue: 'Where is your company headquartered?',
              })}
            </p>
          </div>
        </>
      ) : null}

      {showStory ? (
        <>
          <TextField
            label={t('onboarding.companyInfo.website', {
              defaultValue: 'Website (optional)',
            })}
            value={values.website}
            onChange={(website) => onChange({ website })}
            placeholder="https://example.com"
            type="url"
            autoComplete="off"
          />

          <div>
            <label style={LABEL}>
              {t('onboarding.companyInfo.logo', {
                defaultValue: 'Company logo',
              })}
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: 14,
                borderRadius: 16,
                border: '1px solid var(--input-border)',
                backgroundColor: 'rgba(0,0,0,0.04)',
              }}
            >
              {selectedLogo ? (
                <img
                  src={selectedLogo}
                  alt=""
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    objectFit: 'cover',
                    backgroundColor: '#fff',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    flexShrink: 0,
                  }}
                >
                  <ImagePlus size={18} color="var(--text-tertiary)" />
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 14,
                    lineHeight: '20px',
                    color: 'var(--text-primary)',
                  }}
                >
                  {t('onboarding.companyInfo.logoHint', {
                    defaultValue:
                      'Add a logo to make your page instantly recognizable.',
                  })}
                </div>
                <button
                  onClick={onPickLogo}
                  style={{
                    marginTop: 8,
                    border: 'none',
                    background: 'none',
                    color: 'var(--text-primary)',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: 0,
                    fontFamily: 'inherit',
                  }}
                >
                  {t('onboarding.companyInfo.uploadLogo', {
                    defaultValue: 'Upload logo',
                  })}
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
